// Configuration of calculation formulas
export const FORMULAS = {
  // Management fees
  MANAGEMENT_FEE_RATE: 0.16, // 16%
  
  // VAT rates
  VAT_RATE: 0.20, // 20%
  
  // Platform fees (varying by platform)
  PLATFORM_FEES: {
    'Airbnb': 0.03, // 3%
    'Booking.com': 0.15, // 15%
    'Direct': 0.0, // 0%
    'VRBO': 0.08, // 8%
    'default': 0.05 // 5% default
  },
  
  // Card processing fees
  CARD_FEE_RATE: 0.029, // 2.9%
  CARD_FEE_FIXED: 0.30, // £0.30 fixed fee
  
  // Cleaning and services
  DEFAULT_CLEANING_FEE: 40.0,
  DEFAULT_LAUNDRY_FEE: 15.0,
  
  // Calculate platform fee based on channel
  calculatePlatformFee: (amount, channelName) => {
    const rate = FORMULAS.PLATFORM_FEES[channelName] || FORMULAS.PLATFORM_FEES.default;
    return amount * rate;
  },
  
  // Calculate card processing fee
  calculateCardFee: (amount) => {
    return (amount * FORMULAS.CARD_FEE_RATE) + FORMULAS.CARD_FEE_FIXED;
  },
  
  // Calculate management fee
  calculateManagementFee: (grossProfit) => {
    return grossProfit * FORMULAS.MANAGEMENT_FEE_RATE;
  },
  
  // Calculate VAT
  calculateVAT: (managementFee) => {
    return managementFee * FORMULAS.VAT_RATE;
  },

  /**
   * Calculate VAT breakdown based on business model (Agent vs Principal)
   * @param {Object} params - Calculation parameters
   * @param {string} params.businessModel - 'agent' or 'principal'
   * @param {number} params.managementFees - Management fees amount
   * @param {number} params.consumables - Consumables amount
   * @param {number} params.laundry - Laundry fees amount
   * @param {boolean} params.laundryVatApplicable - Whether VAT applies to laundry
   * @param {number} params.cleaning - Cleaning supplied as service
   * @param {boolean} params.cleaningVatApplicable - Whether VAT applies to cleaning
   * @param {number} params.maintenance - Maintenance costs (Principal only)
   * @param {Array} params.expenses - Array of expense objects with vat info
   * @param {number} params.vatRate - VAT rate (default 20)
   * @returns {Object} VAT breakdown
   */
  calculateVATBreakdown: (params) => {
    const {
      businessModel = 'agent',
      managementFees = 0,
      consumables = 0,
      laundry = 0,
      laundryVatApplicable = true,
      cleaning = 0,
      cleaningVatApplicable = false,
      maintenance = 0,
      expenses = [],
      vatRate = 20
    } = params;

    const rate = vatRate / 100;
    let suppliedServices = {};

    // Management fees always have VAT
    if (managementFees > 0) {
      suppliedServices.management_fees = managementFees;
    }

    if (businessModel === 'agent') {
      // Agent Only Model: VAT on management fees + supplied services only
      if (consumables > 0) {
        suppliedServices.consumables = consumables;
      }
      if (laundry > 0 && laundryVatApplicable) {
        suppliedServices.laundry = laundry;
      }
      if (cleaning > 0 && cleaningVatApplicable) {
        suppliedServices.cleaning = cleaning;
      }
      // Expenses: only if is_vat_applicable = true
      expenses.forEach((expense, index) => {
        if (expense.is_vat_applicable) {
          suppliedServices[`expense_${index}`] = parseFloat(expense.amount || 0);
        }
      });
    } else {
      // Principal Model: VAT on ALL services (auto-enabled)
      if (consumables > 0) {
        suppliedServices.consumables = consumables;
      }
      if (laundry > 0) {
        suppliedServices.laundry = laundry;
      }
      if (cleaning > 0) {
        suppliedServices.cleaning = cleaning;
      }
      if (maintenance > 0) {
        suppliedServices.maintenance = maintenance;
      }
      // All expenses have VAT in Principal model
      expenses.forEach((expense, index) => {
        suppliedServices[`expense_${index}`] = parseFloat(expense.amount || 0);
      });
    }

    const totalTaxable = Object.values(suppliedServices).reduce((sum, val) => sum + val, 0);
    const totalVat = totalTaxable * rate;

    return {
      business_model: businessModel,
      vat_rate: vatRate,
      supplied_services: suppliedServices,
      total_taxable: totalTaxable,
      total_vat: totalVat,
      breakdown: Object.entries(suppliedServices).map(([key, value]) => ({
        service: key,
        net_amount: value,
        vat_amount: value * rate
      }))
    };
  },

  /**
   * Calculate expense VAT based on treatment type
   * @param {Object} expense - Expense object
   * @returns {Object} Calculated amounts
   */
  calculateExpenseVAT: (expense) => {
    const {
      vat_treatment = 'gross',
      amount = 0,
      net_amount = 0,
      vat_rate = 20,
      is_vat_applicable = false
    } = expense;

    if (vat_treatment === 'gross') {
      // Gross amount entered, calculate net if VAT applicable
      if (is_vat_applicable) {
        const rate = vat_rate / 100;
        const netAmount = amount / (1 + rate);
        const vatAmount = amount - netAmount;
        return {
          gross_amount: amount,
          net_amount: netAmount,
          vat_amount: vatAmount,
          vat_rate: vat_rate
        };
      } else {
        return {
          gross_amount: amount,
          net_amount: amount,
          vat_amount: 0,
          vat_rate: 0
        };
      }
    } else {
      // net_plus_vat: Net amount entered separately
      const rate = vat_rate / 100;
      const vatAmount = net_amount * rate;
      const grossAmount = net_amount + vatAmount;
      return {
        gross_amount: grossAmount,
        net_amount: net_amount,
        vat_amount: vatAmount,
        vat_rate: vat_rate
      };
    }
  },

  // Calculate gross profit
  calculateGrossProfit: (totalRevenue, platformFees, cardFees, cleaningFees) => {
    return totalRevenue - platformFees - cardFees - cleaningFees;
  },

  // Calculate settlement to owner
  calculateSettlement: (grossProfit, managementFee, vat, otherExpenses = 0) => {
    return grossProfit - managementFee - vat - otherExpenses;
  }
};

export default FORMULAS;